import React, { useState, useCallback, useRef } from 'react';
import { Upload, FolderOpen, Plus } from 'lucide-react';
import { cn } from '../../../utils/cn';

interface Props {
  onFilesAdded: (files: FileList | File[]) => void;
  disabled?: boolean;
  accept?: string;
  compact?: boolean;
}

export const DropZone: React.FC<Props> = ({
  onFilesAdded,
  disabled = false,
  accept = '*/*',
  compact = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      onFilesAdded(files);
    }
  }, [disabled, onFilesAdded]);

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFilesAdded(e.target.files);
      e.target.value = ''; // Reset for re-selection
    }
  };

  if (compact) {
    return (
      <div
        className={cn(
          'flex items-center justify-center gap-3 p-3',
          'border-2 border-dashed rounded-lg transition-all duration-200 cursor-pointer',
          isDragOver
            ? 'border-blue-500 bg-blue-500/10'
            : 'border-zinc-600 hover:border-zinc-500 bg-zinc-800/50',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled}
        />

        {/* Icon */}
        <div className={cn(
          'flex items-center justify-center w-8 h-8 rounded-full transition-colors',
          isDragOver ? 'bg-blue-500/20' : 'bg-zinc-700'
        )}>
          {isDragOver ? (
            <Plus size={18} className="text-blue-400" />
          ) : (
            <Upload size={18} className="text-zinc-400" />
          )}
        </div>

        {/* Text */}
        <span className="text-sm text-zinc-400">
          {isDragOver ? 'Drop to add files' : 'Drop files here or click to browse'}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative flex flex-col items-center justify-center gap-4 p-8',
        'border-2 border-dashed rounded-lg transition-all duration-200 cursor-pointer',
        'min-h-[280px]',
        isDragOver
          ? 'border-blue-500 bg-blue-500/10'
          : 'border-zinc-600 hover:border-zinc-500 bg-zinc-800/50',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Icon */}
      <div className={cn(
        'flex items-center justify-center w-16 h-16 rounded-full transition-colors',
        isDragOver ? 'bg-blue-500/20' : 'bg-zinc-700'
      )}>
        {isDragOver ? (
          <Plus size={32} className="text-blue-400" />
        ) : (
          <Upload size={32} className="text-zinc-400" />
        )}
      </div>

      {/* Text */}
      <div className="text-center">
        <p className="text-base font-medium text-zinc-200">
          {isDragOver ? 'Drop files here' : 'Drag & drop files here'}
        </p>
        <p className="mt-1 text-sm text-zinc-400">
          or click to browse your files
        </p>
      </div>

      {/* Supported formats */}
      <div className="flex flex-wrap justify-center gap-2 mt-2">
        {['PNG', 'JPG', 'GIF', 'WebP', 'MP3', 'OGG', 'WAV', 'JS', 'TS', 'TTF', 'JSON'].map((format) => (
          <span
            key={format}
            className="px-2 py-0.5 text-xs bg-zinc-700 text-zinc-400 rounded"
          >
            {format}
          </span>
        ))}
      </div>

      {/* Browse button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          fileInputRef.current?.click();
        }}
        className={cn(
          'flex items-center gap-2 px-4 py-2 mt-2',
          'bg-blue-600 hover:bg-blue-500 text-white rounded-lg',
          'transition-colors text-sm font-medium',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        disabled={disabled}
      >
        <FolderOpen size={16} />
        Browse Files
      </button>
    </div>
  );
};
